import { fs } from '@/theme/tokens';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import type { SvgIconComponent } from '@mui/icons-material';
import GridViewIcon from '@mui/icons-material/GridView';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import UndoIcon from '@mui/icons-material/Undo';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import PaidIcon from '@mui/icons-material/Paid';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TuneIcon from '@mui/icons-material/Tune';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LoopIcon from '@mui/icons-material/Loop';
import HistoryIcon from '@mui/icons-material/History';
import BuildIcon from '@mui/icons-material/Build';
import CalculateIcon from '@mui/icons-material/Calculate';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { APP_BASE } from '@/lib/api-base';
import logoUrl from '@/assets/uknowva-hrms-logo.png';
import logoMarkUrl from '@/assets/uknowva-mark.png';
import { useEmployeeOnly } from '@/store/auth';
import { useEntityStore } from '@/store/entity';
import { useNavMenu, type NavMenuNode } from '@/api/nav-menu';
import { useActiveBranding } from '@/api/settings';
import { useSidebarStore } from '@/store/sidebar';

/** Default rail colour when the active entity has no brand colour set. */
const DEFAULT_BRAND = '#108ae6';

type Props = {
  width: number;
  /** Rendered inside the mobile Drawer — force expanded, hide the collapse toggle. */
  inDrawer?: boolean;
  /** Called when a nav item is clicked (closes the mobile drawer). */
  onNavigate?: () => void;
};

const COLLAPSED_WIDTH = 56;
const EXPANDED_KEY = 'ukn_sidebar_expanded_groups';

/**
 * Sidebar — flex item (NOT position:fixed) with the legacy `.sb` look:
 *   - Solid blue background #108ae6, white text, ~230 px wide
 *   - Group labels: 9 px uppercase letter-spaced @ 35% white
 *   - Items: 7 px / 10 px padding, 13 px font, plain emoji icons
 *   - Active: 25%-white overlay + 4 px white left tick (legacy `.sbi.on`)
 *   - Hover: 15%-white overlay
 *   - Width fixed via `flex-shrink: 0`; never compresses
 *
 * The tree itself comes from the tenant's database (`GET /api/nav/menu`), so
 * an admin can rename, reorder, hide or role-gate entries without a release.
 * `FALLBACK_GROUPS` below is the same tree compiled in: it renders while the
 * request is in flight and stays put if the call fails, because a sidebar
 * that renders nothing is a dead application.
 *
 * A menu node with children and `is_collapsible` renders as a dropdown —
 * that is what the Challan section uses.
 */

type Item = {
  key: string;
  to: string;
  label: string;
  ic: string;
  badge?: string | null;
  collapsible?: boolean;
  children?: Item[];
};
/** A group renders as a static uppercase label unless `collapsible` is set,
 *  in which case it becomes a dropdown header (Challan works this way). */
type Group = {
  key: string;
  label: string;
  items: Item[];
  collapsible?: boolean;
  ic?: string;
  badge?: string | null;
};

/** Bundled copy of the shipped menu — used until (or instead of) the API. */
/**
 * Wireframe icon set. Keyed by the stable nav `key` so it survives label and
 * route edits. `ic` itself stays a string because the server-driven menu
 * (NavMenuNode.icon) supplies one — anything not listed here falls back to
 * rendering that string verbatim, so custom/server entries still show.
 */
const NAV_ICONS: Record<string, SvgIconComponent> = {
  ess_dashboard: GridViewIcon,
  ess_payslips: ReceiptLongIcon,
  ess_salary: PaidIcon,
  ess_declarations: DescriptionIcon,
  ess_flexi: VolunteerActivismIcon,
  ess_reimbursements: ReceiptIcon,
  ess_loans: AccountBalanceWalletIcon,
  ess_attendance: EventAvailableIcon,
  ess_lop_reversal: UndoIcon,
  ess_profile: PersonIcon,
  home_dashboard: GridViewIcon,
  home_payroll_config: SettingsIcon,
  home_employees: PeopleIcon,
  home_payroll_runs: RocketLaunchIcon,
  home_approvals: CheckCircleIcon,
  home_reports: BarChartIcon,
  sal_structures: AccountTreeIcon,
  sal_pay_items: ListAltIcon,
  ops_attendance: EventAvailableIcon,
  ops_shifts: ScheduleIcon,
  ops_overtime: MoreTimeIcon,
  ops_incentives: PaidIcon,
  ops_flexi: VolunteerActivismIcon,
  ops_reimbursements: ReceiptIcon,
  ops_loans: AccountBalanceWalletIcon,
  ops_perquisites: DirectionsCarIcon,
  ops_esop: TrendingUpIcon,
  ops_fnf: ExitToAppIcon,
  ops_arrears: StackedLineChartIcon,
  ops_lop_reversals: UndoIcon,
  grp_challan: CalculateIcon,
  challan_batches: Inventory2Icon,
  challan_entries: ReceiptIcon,
  challan_company_info: BusinessIcon,
  challan_certificates: WorkspacePremiumIcon,
  challan_etds_returns: AssuredWorkloadIcon,
  tax_it_declarations: DescriptionIcon,
  tax_tds: ReceiptLongIcon,
  tax_statutory_returns: AccountBalanceIcon,
  tax_rules_studio: TuneIcon,
  tax_statutory: GavelIcon,
  org_departments: BusinessIcon,
  org_designations: SchoolIcon,
  org_grade_bands: LinearScaleIcon,
  org_leave_types: LocalOfferIcon,
  sys_users: ManageAccountsIcon,
  sys_workflows: LoopIcon,
  sys_audit_log: HistoryIcon,
  sys_settings: BuildIcon,
};

/** Renders the mapped icon, or the raw `ic` string when there is no mapping. */
function NavIcon({ itemKey, ic }: { itemKey: string; ic: string }) {
  const Ico = NAV_ICONS[itemKey];
  return (
    <Box component="span" sx={{
      width: 18, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: fs.base, textAlign: 'center',
    }}>
      {Ico
        ? <Ico sx={{ fontSize: fs.icon }} />
        // Unmapped (dynamic/server) entries fall back to an emoji; render it
        // monochrome so it matches the white icons instead of showing in colour.
        : <Box component="span" sx={{ filter: 'grayscale(1) brightness(0) invert(1)', opacity: 0.85 }}>{ic}</Box>}
    </Box>
  );
}

const FALLBACK_ESS_GROUPS: Group[] = [
  {
    key: 'grp_ess_self_service',
    label: 'Self-service',
    items: [
      // Ordered by how often an employee needs it: pay first, then time,
      // then the regular claim/benefit actions, then seasonal tax, then
      // loans and the rare LOP reversal, with Profile last.
      { key: 'ess_dashboard',      to: '/me',                label: 'Dashboard',       ic: '🏠' },
      { key: 'ess_payslips',       to: '/me/payslips',       label: 'My Payslips',     ic: '🧾' },
      { key: 'ess_salary',         to: '/me/salary',         label: 'My Salary Structure', ic: '💰' },
      { key: 'ess_attendance',     to: '/me/attendance',     label: 'My Attendance',   ic: '📅' },
      // Hidden from the employee site for now; uncomment to bring it back.
      // { key: 'ess_reimbursements', to: '/me/reimbursements', label: 'Reimbursements',  ic: '🧾' },
      { key: 'ess_flexi',          to: '/me/flexi',          label: 'Flexi Benefits',  ic: '🎁' },
      { key: 'ess_declarations',   to: '/me/declarations',   label: 'IT Declarations', ic: '📑' },
      { key: 'ess_loans',          to: '/me/loans',          label: 'My Loans',        ic: '💳' },
      { key: 'ess_lop_reversal',   to: '/me/lop-reversal',   label: 'LOP Reversal',    ic: '↺' },
      { key: 'ess_profile',        to: '/me/profile',        label: 'My Profile',      ic: '👤' },
    ],
  },
];

const FALLBACK_GROUPS: Group[] = [
  {
    key: 'grp_home',
    label: 'Home',
    items: [
      { key: 'home_dashboard',      to: '',                 label: 'Dashboard',      ic: '🏠' },
      { key: 'home_employees',      to: '/employees',       label: 'Employees',      ic: '👥' },
      { key: 'home_payroll_runs',   to: '/payroll-runs',    label: 'Payroll Runs',   ic: '🚀' },
      { key: 'home_approvals',      to: '/approvals',       label: 'Approvals',      ic: '✅' },
      { key: 'home_reports',        to: '/reports',         label: 'Reports',        ic: '📊' },
      { key: 'home_payroll_config', to: '/payroll-config',  label: 'Payroll Config', ic: '⚙' },
    ],
  },
  {
    key: 'grp_salary_structure',
    label: 'Salary Structure',
    items: [
      { key: 'sal_structures', to: '/salary-structures', label: 'Salary Structures', ic: '📐' },
      { key: 'sal_pay_items',  to: '/pay-items',         label: 'Pay Items',         ic: '📋' },
    ],
  },
  {
    key: 'grp_operations',
    label: 'Operations',
    items: [
      { key: 'ops_attendance',     to: '/attendance',      label: 'Attendance',      ic: '📅' },
      { key: 'ops_shifts',         to: '/shifts',          label: 'Shifts',          ic: '⏰' },
      { key: 'ops_ot_rules',       to: '/ot-rules',        label: 'OT Rules',        ic: '⏱' },
      { key: 'ops_incentives',     to: '/incentives',      label: 'Variable Pay',    ic: '💰' },
      { key: 'ops_flexi',          to: '/flexi-benefits',  label: 'Flexi Benefits',  ic: '🎁' },
      { key: 'ops_reimbursements', to: '/reimbursements',  label: 'Reimbursements',  ic: '🧾' },
      { key: 'ops_loans',          to: '/loans-advances',  label: 'Loans & Advances', ic: '💳' },
      { key: 'ops_perquisites',    to: '/perquisites',     label: 'Perquisites',     ic: '🚗' },
      { key: 'ops_esop',           to: '/esop-management', label: 'ESOP Management', ic: '📈' },
      { key: 'ops_fnf',            to: '/fnf',             label: 'FnF Settlement',  ic: '🚪' },
      { key: 'ops_arrears',        to: '/arrears',         label: 'Arrears',         ic: '📈' },
      { key: 'ops_lop_reversals',  to: '/lop-reversals',   label: 'LOP Reversal',    ic: '↺' },
    ],
  },
  {
    key: 'grp_challan',
    label: 'Challan',
    collapsible: true,
    ic: '🧮',
    items: [
      { key: 'challan_batches',      to: '/challan/batches',      label: 'Challan Batch', ic: '📦' },
      { key: 'challan_entries',      to: '/challan/entries',      label: 'Challan Entry', ic: '🧾' },
      { key: 'challan_company_info', to: '/challan/company-info', label: 'Company Info',  ic: '🏢' },
      { key: 'challan_certificates', to: '/challan/certificates', label: 'Lower Deduction Certificates', ic: '📜' },
      { key: 'challan_etds_returns', to: '/challan/etds-returns', label: 'eTDS Returns',  ic: '📤' },
    ],
  },
  {
    key: 'grp_tax_compliance',
    label: 'Tax & Compliance',
    items: [
      { key: 'tax_it_declarations',   to: '/it-declarations',   label: 'IT Declarations',      ic: '📑' },
      { key: 'tax_tds',               to: '/tds-tax',           label: 'TDS / Tax',            ic: '🧾' },
      { key: 'tax_statutory_returns', to: '/statutory-returns', label: 'Statutory Returns',    ic: '🏛' },
      { key: 'tax_rules_studio',      to: '/rules-studio',      label: 'Rules Studio',         ic: '⚙' },
      { key: 'tax_statutory',         to: '/statutory',         label: 'Statutory compliance', ic: '⚖' },
    ],
  },
  {
    key: 'grp_org_masters',
    label: 'Org Masters',
    items: [
      { key: 'org_departments',  to: '/departments',  label: 'Departments',  ic: '🏬' },
      { key: 'org_designations', to: '/designations', label: 'Designations', ic: '🎓' },
      { key: 'org_grade_bands',  to: '/grade-bands',  label: 'Grade Bands',  ic: '🎚' },
      { key: 'org_leave_types',  to: '/leave-types',  label: 'Leave Types',  ic: '🏷' },
    ],
  },
  {
    key: 'grp_system',
    label: 'System',
    items: [
      { key: 'sys_users',     to: '/users',     label: 'Users & Roles',      ic: '👤' },
      { key: 'sys_workflows', to: '/workflows', label: 'Approval Workflows', ic: '🔁' },
      { key: 'sys_audit_log', to: '/audit-log', label: 'Audit Log',          ic: '📜' },
      { key: 'sys_settings',  to: '/settings',  label: 'Settings',           ic: '🔧' },
    ],
  },
];

/** API node → the shape this component renders. */
function toItem(n: NavMenuNode): Item {
  return {
    key: n.item_key,
    to: n.route ?? '',
    label: n.label,
    ic: n.icon || '•',
    badge: null,  // nav badges intentionally suppressed (no 'NEW' pills)
    collapsible: n.is_collapsible,
    children: n.children?.length ? n.children.map(toItem) : undefined,
  };
}

function toGroups(items: NavMenuNode[]): Group[] {
  return items.map((g) => ({
    key: g.item_key,
    label: g.label,
    collapsible: g.is_collapsible,
    ic: g.icon || undefined,
    badge: null,  // nav badges intentionally suppressed
    items: (g.children ?? []).map(toItem),
  }));
}


/** Does `item` (or anything under it) point at the current URL? */
function containsPath(item: Item, pathname: string): boolean {
  const full = `${APP_BASE}${item.to}`;
  if (item.to && (pathname === full || pathname.startsWith(`${full}/`))) return true;
  return (item.children ?? []).some((c) => containsPath(c, pathname));
}

function filterItems(items: Item[], term: string): Item[] {
  const out: Item[] = [];
  for (const it of items) {
    const selfHit = it.label.toLowerCase().includes(term);
    const kids = filterItems(it.children ?? [], term);
    // A parent that matches keeps all its children; otherwise only the
    // children that matched survive, so searching "company" lands directly
    // on Challan › Company Info.
    if (selfHit) out.push(it);
    else if (kids.length) out.push({ ...it, children: kids });
  }
  return out;
}

export function Sidebar({ width, inDrawer = false, onNavigate }: Props) {
  const employeeOnly = useEmployeeOnly();
  const entityId = useEntityStore((s) => s.entityId);
  const location = useLocation();
  const menu = useNavMenu(entityId);

  // Per-entity branding drives the rail: its colour tints the whole sidebar and
  // its logo replaces the default uKnowva wordmark. Switching the topbar entity
  // re-fires this query, so the chrome re-skins to that entity instantly.
  const branding = useActiveBranding(entityId);
  const brandColor = branding?.primary_color || DEFAULT_BRAND;
  const customLogo = branding?.logo_b64 || null;
  // The collapsed rail shows the small logo, when there is one.
  const customMark = branding?.logo_icon_b64 || null;
  // Labels and icons take the branded sidebar text colour; every wash and
  // divider on the rail is that colour at the strength white used to be.
  const fg = branding?.sidebar_text_color || '#ffffff';
  const onRail = (strength: number) => alpha(fg, strength);
  // The logo band lines up with the topbar, so it wears the header colour.
  const headerBg = branding?.header_color || '#fff';

  const groups = useMemo<Group[]>(() => {
    const fromApi = menu.data?.items?.length ? toGroups(menu.data.items) : null;
    if (fromApi && fromApi.length) return fromApi;
    // The server named pages this person may not open, so an empty menu is theirs (a company where they open
    // nothing), not a failed read; the bundled tree would list every page, each refused.
    if (menu.data?.denied?.length) return [];
    return employeeOnly ? FALLBACK_ESS_GROUPS : FALLBACK_GROUPS;
  }, [menu.data, employeeOnly]);

  const collapsedPref = useSidebarStore((s) => s.collapsed);
  const setCollapsed = useSidebarStore((s) => s.setCollapsed);
  // In the mobile drawer the rail is always fully expanded.
  const collapsed = inDrawer ? false : collapsedPref;
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(EXPANDED_KEY) || '{}'); } catch { return {}; }
  });
  const searchRef = useRef<HTMLInputElement | null>(null);
  const focusAfterExpand = useRef(false);

  const setCollapsedPersisted = (next: boolean) => {
    setCollapsed(next);
    // Collapsing hides the search field — drop the filter so re-expanding
    // never shows a partially filtered tree with no visible reason why.
    if (next) setQ('');
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Groups default to open via computed state (first group / active route), so
  // a plain toggle of the stored value would need two clicks the first time.
  // Set the explicit boolean from the current effective open state instead.
  const setGroupOpen = (key: string, next: boolean) => {
    setExpanded((prev) => {
      const n = { ...prev, [key]: next };
      try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(n)); } catch { /* ignore */ }
      return n;
    });
  };

  // Clicking the search icon while collapsed expands first, then focuses the
  // input once it has actually been mounted by the re-render.
  const openSearch = () => {
    focusAfterExpand.current = true;
    setCollapsedPersisted(false);
  };
  useEffect(() => {
    if (!collapsed && focusAfterExpand.current) {
      focusAfterExpand.current = false;
      searchRef.current?.focus();
    }
  }, [collapsed]);

  // Filter menu items by label (and group label, so typing "tax" keeps the
  // whole Tax & Compliance group). Groups with no surviving items drop out.
  const term = q.trim().toLowerCase();
  const visibleGroups = useMemo<Group[]>(() => {
    if (!term) return groups;
    return groups
      .map((g) => {
        if (g.label.toLowerCase().includes(term)) return g;
        return { ...g, items: filterItems(g.items, term) };
      })
      .filter((g) => g.items.length > 0);
  }, [groups, term]);

  /** A dropdown is open when the user opened it, when the current page lives
   *  inside it, or while a search is narrowing the tree. */
  const isOpen = (it: Item) =>
    !!term || expanded[it.key] || containsPath(it, location.pathname);

  const linkStyle = (isActive: boolean, depth: number) => ({
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: collapsed ? 0 : 13,
    padding: collapsed ? '13px 0' : `13px 16px 13px ${16 + depth * 14}px`,
    marginBottom: 3,
    fontSize: depth ? 13 : 14,
    lineHeight: 1.3,
    fontWeight: isActive ? 700 : 400,
    // No heavy background block on the active row — wayfinding comes from a
    // bright bold label + a crisp left tick, which reads cleaner on the blue.
    color: isActive ? fg : onRail(0.82),
    // background is left to CSS (see the nav container's `& .ukn-navlink`
    // rules) so hover can apply — an inline background would out-rank it.
    textDecoration: 'none',
    transition: 'background 150ms, color 150ms',
    borderLeft: isActive ? '3px solid #ffffff' : '3px solid transparent',
  });

  const badgeChip = (badge: string) => (
    <Box component="span" sx={{
      fontSize: fs.micro,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      px: 0.6, py: 0.1,
      borderRadius: 0.5,
      background: badge === 'new' ? onRail(0.30) : onRail(0.15),
      color: fg,
    }}>
      {badge}
    </Box>
  );

  const renderItem = (it: Item, depth = 0): React.ReactNode => {
    const hasKids = !!it.children?.length;

    // Collapsed rail: a dropdown has no room for its header, so its children
    // are rendered directly — every destination stays one click away.
    if (hasKids && collapsed) {
      return <Box key={it.key}>{it.children!.map((c) => renderItem(c, depth))}</Box>;
    }

    if (hasKids && it.collapsible) {
      const open = isOpen(it);
      const active = containsPath(it, location.pathname);
      return (
        <Box key={it.key}>
          <Box
            component="button"
            type="button"
            onClick={() => toggleExpanded(it.key)}
            aria-expanded={open}
            sx={{
              ...linkStyle(active && !open, depth),
              // `linkStyle` is authored for the plain `style` prop, where a
              // numeric `gap: 8` means 8px. Spread into MUI's `sx` here it is
              // re-read as theme spacing (8 × 8 = 64px), which overflows the
              // row and squeezes the flex:1 label to zero width — the Challan
              // header rendered icon + badge but no text. Pin it back to 8px.
              gap: '8px',
              width: '100%',
              border: 0,
              borderLeft: active ? '3px solid #ffffff' : '3px solid transparent',
              // Explicit transparent base — a native <button> otherwise shows
              // the browser's default grey, which read as a stuck highlight.
              background: 'transparent',
              color: fg,
              cursor: 'pointer',
              font: 'inherit',
              fontSize: fs.base,
              fontWeight: active ? 700 : 400,
              marginBottom: 0,   // no gap before the expanded children
              textAlign: 'left',
              transition: 'background 150ms, color 150ms',
              '&:hover': { background: onRail(0.10) },
            }}
          >
            <NavIcon itemKey={it.key} ic={it.ic} />
            <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.label}
            </Box>
            {it.badge && badgeChip(it.badge)}
            {open
              ? <ExpandLessIcon sx={{ fontSize: 16, opacity: 0.8 }} />
              : <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.8 }} />}
          </Box>
          <Collapse in={open} timeout={140} unmountOnExit>
            {/* Nested items sit on the same blue as the rail — a subtle left
                rule + their indent signals the grouping, instead of a dark
                inset panel that read as a different shade. */}
            <Box sx={{
              ml: '24px', my: '4px',
              borderLeft: `1px solid ${onRail(0.16)}`,
              // Compact the nested rows so the dropdown doesn't leave big gaps
              // above the first child and below the last.
              '& .ukn-navlink': { paddingTop: '9px', paddingBottom: '9px', marginBottom: '1px', paddingLeft: '14px' },
            }}>
              {it.children!.map((c) => renderItem(c, depth + 1))}
            </Box>
          </Collapse>
        </Box>
      );
    }

    // A container that is not collapsible (or has no route of its own) just
    // renders its children inline under the group.
    if (hasKids && !it.to) {
      return <Box key={it.key}>{it.children!.map((c) => renderItem(c, depth))}</Box>;
    }

    return (
      <Tooltip
        key={it.key}
        title={collapsed ? it.label : ''}
        placement="right"
        arrow
        disableHoverListener={!collapsed}
      >
        <NavLink
          className="ukn-navlink"
          to={`${APP_BASE}${it.to}`}
          // Exact-match the two "home" roots ('' = admin, '/me' = ESS) so they
          // don't stay highlighted on every child route (e.g. /me/profile).
          end={it.to === '' || it.to === '/me'}
          onClick={() => onNavigate?.()}
          style={({ isActive }) => linkStyle(isActive, depth)}
        >
          <NavIcon itemKey={it.key} ic={it.ic} />
          {!collapsed && (
            <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.label}
            </Box>
          )}
          {!collapsed && it.badge && badgeChip(it.badge)}
        </NavLink>
      </Tooltip>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : width,
        flexShrink: 0,
        bgcolor: brandColor,
        display: 'flex',
        flexDirection: 'column',
        // The rail itself does not scroll — the logo band and search stay
        // pinned; only the nav-groups list below scrolls (see the Groups Box).
        overflow: 'hidden',
        transition: 'width 160ms ease, background-color 200ms',
        // Subtle hover wash on nav rows (active row keeps just its left tick).
        '& .ukn-navlink:hover': { background: onRail(0.10) },
      }}
    >
      {/* Brand block — a white band at the header height so the top-left
          corner is white and lines up with the topbar (no blue behind the
          logo). The collapse toggle now lives in the header. */}
      <Box sx={{
        height: 60, flexShrink: 0,
        background: headerBg,
        borderBottom: '1px solid #e6ebf0',
        // Slight shadow onto the blue rail below for a clean separation.
        boxShadow: '0 2px 6px rgba(24,40,72,0.10)',
        position: 'relative', zIndex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        px: collapsed ? 0.5 : 2,
      }}>
        <Box
          component="a"
          href={`${APP_BASE}/`}
          aria-label="uKnowva: go to dashboard"
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            textDecoration: 'none', width: '100%',
          }}
        >
          {/* Collapsed rail is too narrow for the wordmark — swap to the
              square mark. Imported as modules so Vite emits correct URLs. */}
          <Box
            component="img"
            src={collapsed ? (customMark ?? customLogo ?? logoMarkUrl) : (customLogo ?? logoUrl)}
            alt={customLogo || customMark ? 'Entity logo' : 'uKnowva'}
            sx={{
              height: collapsed ? 30 : 34,
              width: 'auto', maxWidth: '100%',
              objectFit: 'contain', display: 'block',
            }}
          />
        </Box>
      </Box>

      {/* Search — filters the menu tree below. Collapsed mode swaps it for an
          icon that re-opens the sidebar and focuses the field. */}
      <Box sx={{
        flexShrink: 0,
        px: collapsed ? 0 : 2,
        pt: 1.75, pb: 0.25,
        display: 'flex',
        justifyContent: 'center',
      }}>
        {collapsed ? (
          <Tooltip title="Search menu" placement="right" arrow>
            <IconButton
              onClick={openSearch}
              size="small"
              aria-label="Search menu"
              sx={{
                color: onRail(0.85),
                '&:hover': { background: onRail(0.18), color: fg },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: '13px',
            width: '100%',
            height: 44, px: 2,
            borderRadius: '8px',
            background: 'rgba(0,0,0,0.13)',
            border: `1px solid ${onRail(0.13)}`,
            transition: 'background 150ms, border-color 150ms',
            '&:hover': { background: 'rgba(0,0,0,0.17)' },
            '&:focus-within': {
              background: 'rgba(0,0,0,0.20)',
              borderColor: onRail(0.34),
            },
          }}>
            <SearchIcon sx={{ fontSize: 18, color: fg, flexShrink: 0 }} />
            <InputBase
              inputRef={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setQ(''); } }}
              placeholder="Search menu…"
              inputProps={{ 'aria-label': 'Search menu' }}
              sx={{
                flex: 1, minWidth: 0,
                color: fg,
                fontSize: fs.base,
                '& input::placeholder': { color: onRail(0.92), opacity: 1 },
              }}
            />
            {q && (
              <IconButton
                onClick={() => { setQ(''); searchRef.current?.focus(); }}
                size="small"
                aria-label="Clear search"
                sx={{ p: 0.125, color: onRail(0.8), '&:hover': { color: fg } }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {/* Groups — the only scrollable region of the rail, so the logo band
          and search above stay pinned while a long menu scrolls. */}
      <Box sx={{
        flex: 1, minHeight: 0, py: 0.5,
        overflowY: 'auto', overflowX: 'hidden',
        '&::-webkit-scrollbar': { width: 0, height: 0 },
        scrollbarWidth: 'none',
      }}>
        {!collapsed && term && visibleGroups.length === 0 && (
          <Box sx={{
            px: 2.5, py: 2,
            fontSize: fs.small, color: onRail(0.70), lineHeight: 1.5,
          }}>
            No menu items match “{q.trim()}”.
          </Box>
        )}
        {visibleGroups.map((g, gi) => {
          // Collapsed rail: no labels, just a divider between groups; items
          // (icon-only) always show.
          if (collapsed) {
            return (
              <Box key={g.key}>
                <Box sx={{ height: '1px', mx: 1.5, mt: 1.5, mb: 0.5, bgcolor: onRail(0.18) }} />
                {g.items.map((it) => renderItem(it))}
              </Box>
            );
          }
          // Every group — including server "collapsible" ones like Challan —
          // renders the same way: an uppercase section label that folds its
          // items (wireframe .nav-toggle), for a uniform rail.
          // Open when: searching, or explicitly toggled, or (no explicit
          // state) it is the first group or holds the active route. Folding
          // keeps the ~40 admin items from becoming one long scroll.
          const hasActive = g.items.some((it) => containsPath(it, location.pathname));
          const open = !!term || (g.key in expanded ? !!expanded[g.key] : (gi === 0 || hasActive));
          return (
            <Box key={g.key}>
              <Box
                component="button"
                type="button"
                onClick={() => setGroupOpen(g.key, !open)}
                aria-expanded={open}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 1,
                  background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'inherit',
                  px: '17px', pt: '22px', pb: '8px',
                  fontSize: fs.micro, fontWeight: 600, letterSpacing: '1px',
                  textTransform: 'uppercase', color: onRail(0.55),
                  transition: 'color .15s',
                  '&:hover': { color: onRail(0.88) },
                }}
              >
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{g.label}</Box>
                <ExpandMoreIcon sx={{
                  fontSize: fs.md, flexShrink: 0, transition: 'transform .15s',
                  transform: open ? 'none' : 'rotate(-90deg)',
                }} />
              </Box>
              {open && g.items.map((it) => renderItem(it))}
            </Box>
          );
        })}
      </Box>

    </Box>
  );
}
